import React, { useState, useEffect } from 'react'
import { IEpiProps, StepConstants, IDateTimeEvent, IUserSelections, FetchEventDetailsOptions } from '../models/event-reg-refresh-models'
import { useEventRegRefreshContext, useEventRegRefreshDispatch } from '../reducers/event-reg-refresh-reducers'
import { ProgramSummary } from './program-summary'
import { DateTimeCard } from './date-time-card';
import { ShareSelections } from './share-selections';
import { Modal } from './modal';
import { ContentWrapper } from './layout/content-wrapper';
import { MainContent } from './layout/main-content';
import { SideBarContent } from './layout/sidebar-content';
import { LocationFilter } from './location-filter';
import { MonthYearFilter } from './month-year-filter';
import { LoadingSpinner } from './loading-spinner';
import { resolveUrl } from '../helpers/helpers';

interface DateTimeStepProps {
  // Core functionality to be provided by parent
  fetchEventDetails: (options?: FetchEventDetailsOptions) => Promise<IDateTimeEvent[]>;
  handleEventSelection: (event: IDateTimeEvent) => void;
  handleContinue: () => void;
  isEventSelected: (eventNumber: string) => boolean;
  isEventSelectedForValidation: () => boolean;
  title: string;
  description: string;
  validationMessage: string;
  currentStep: string;
  targetStep: string;
  programName: string;
  dateTimeStepIntroMessage?: string;
  shouldShowOnlyShared: boolean;
  handleShowAllPrograms: () => void;
  sharedDateTimeEvent?: IDateTimeEvent | null;
  // Filter state passed from parent
  cityStateInput?: string;
  postalCodeInput?: string;
  rangeInput?: string;
  selectedDates?: string[];
  sortByDate?: boolean;
  setCityStateInput?: (value: string) => void;
  setPostalCodeInput?: (value: string) => void;
  setRangeInput?: (value: string) => void;
  setSelectedDates?: (dates: string[]) => void;
  setSortByDate?: (value: boolean) => void;
}

interface IContextProps {
  epiProps: IEpiProps
  userSelections: IUserSelections
}

export const DateTimeStep: React.FC<DateTimeStepProps> = ({
  fetchEventDetails,
  handleEventSelection,
  handleContinue,
  isEventSelected,
  isEventSelectedForValidation,
  title,
  description,
  validationMessage,
  currentStep,
  targetStep,
  programName,
  dateTimeStepIntroMessage,
  shouldShowOnlyShared,
  handleShowAllPrograms,
  sharedDateTimeEvent,
  // Destructure filter state and setter functions from props
  cityStateInput: cityStateInputProp,
  postalCodeInput: postalCodeInputProp,
  rangeInput: rangeInputProp,
  selectedDates: selectedDatesProp,
  sortByDate: sortByDateProp,
  setCityStateInput: setCityStateInputProp,
  setPostalCodeInput: setPostalCodeInputProp,
  setRangeInput: setRangeInputProp,
  setSelectedDates: setSelectedDatesProp,
  setSortByDate: setSortByDateProp
}) => {
  const { epiProps, userSelections, eventRegistrationRefreshModel } = useEventRegRefreshContext()!
  const dispatch = useEventRegRefreshDispatch()!
  const [isLoading, setIsLoading] = useState(false)
  const [dateTimeEventList, setDateTimeEventList] = useState<IDateTimeEvent[]>([])
  const [showValidationMessage, setShowValidationMessage] = useState(false)
  const [showShareSelectionsModal, setShowShareSelectionsModal] = useState(false)

  // Use props if provided, otherwise use local state (for backward compatibility)
  const [localCityStateInput, setLocalCityStateInput] = useState('')
  const [localPostalCodeInput, setLocalPostalCodeInput] = useState('')
  const [localRangeInput, setLocalRangeInput] = useState('')
  const [localSelectedDates, setLocalSelectedDates] = useState<string[]>([])
  const [localSortByDate, setLocalSortByDate] = useState(true)

  const cityStateInput = cityStateInputProp ?? localCityStateInput
  const postalCodeInput = postalCodeInputProp ?? localPostalCodeInput
  const rangeInput = rangeInputProp ?? localRangeInput
  const selectedDates = selectedDatesProp ?? localSelectedDates
  const sortByDate = sortByDateProp ?? localSortByDate

  const setCityStateInput = setCityStateInputProp ?? setLocalCityStateInput
  const setPostalCodeInput = setPostalCodeInputProp ?? setLocalPostalCodeInput
  const setRangeInput = setRangeInputProp ?? setLocalRangeInput
  const setSelectedDates = setSelectedDatesProp ?? setLocalSelectedDates
  const setSortByDate = setSortByDateProp ?? setLocalSortByDate
  
  const [locationFilterOpen, setLocationFilterOpen] = useState(false)
  const [monthYearFilterOpen, setMonthYearFilterOpen] = useState(false)
  const [initialEventList, setInitialEventList] = useState<IDateTimeEvent[]>([])
  const [showAllInitialEvents, setShowAllInitialEvents] = useState(false)
  const [hasAppliedFilters, setHasAppliedFilters] = useState(false)
  const [isLoadingResultsAfterApplyingFilters, setIsLoadingResultsAfterApplyingFilters] = useState(false)

  const onApplyFiltersButtonClick = (isInitialLoad = false, sortOverride?: string, city = cityStateInput, postal = postalCodeInput) => {
    setIsLoadingResultsAfterApplyingFilters(true) //Sets loading spinner on 'apply filters' CTA
    
    // Save filters to userSelections context when applying filters
    if (!isInitialLoad) {
      const updatedUserSelections = {
        ...userSelections,
        filters: {
          cityState: city,
          postalCode: postal,
          rangeInput,
          selectedDates,
          sortByDate: sortOverride ? (sortOverride === 'date') : sortByDate
        }
      };
      dispatch({ type: 'updateUserSelections', data: updatedUserSelections });
    }
    
    loadEvents(isInitialLoad, sortOverride, city, postal);
  }

  // Wrapper for fetchEventDetails that handles loading state
  const loadEvents = async (isInitialLoad = false, sortOverride?: string, city = cityStateInput, postal = postalCodeInput) => {
    setIsLoading(true)

    try {
      // Always pass current filter state, even on initial load
      // This ensures restored filters from userSelections are applied
      const options: FetchEventDetailsOptions = {
        locationFilter: eventRegistrationRefreshModel.distanceUnit ? city.trim() : '',
        postalCodeFilter: eventRegistrationRefreshModel.distanceUnit ? postal.trim() : '',
        rangeFilter: eventRegistrationRefreshModel.distanceUnit ? rangeInput : '',
        datesFilter: selectedDates,
        sortBy: sortOverride ?? (sortByDate ? 'date' : 'location')
      }

      const events = await fetchEventDetails(options)
      setDateTimeEventList(Array.isArray(events) ? events : [])

      // Track if any filters have been applied (check on both initial and subsequent loads)
      const filtersApplied = (Boolean(eventRegistrationRefreshModel.distanceUnit) && (city !== '' || postal !== '' || rangeInput !== '')) || selectedDates.length > 0
      setHasAppliedFilters(filtersApplied)

      // Store initial events for filter visibility logic
      if (isInitialLoad) {
        setInitialEventList(events)
        setShowAllInitialEvents(false)
      }
    } catch (err) {
      console.error('Error fetching event details:', err)
    } finally {
      setIsLoading(false)
      setIsLoadingResultsAfterApplyingFilters(false)
    }
  }

  // Call API when component mounts or when step becomes active
  useEffect(() => {
    if (currentStep === targetStep) {
      loadEvents(true) // Mark as initial load
    }
  }, [currentStep, targetStep])

  // Handle continue button click with validation
  const handleContinueWithValidation = () => {
    if (!isEventSelectedForValidation()) {
      setShowValidationMessage(true)
      return
    }

    setShowValidationMessage(false)
    handleContinue()
  }

  const allEventsVirtual = (events: IDateTimeEvent[]): boolean => {
    if (!events || events.length === 0) {
      return false;
    }

    return events.every((event) => event.isVirtual === true);
  };

  const handleDateSelection = (monthValue: string, isChecked: boolean) => {
    if (isChecked) {
      setSelectedDates!([...selectedDates, monthValue]);
    } else {
      setSelectedDates!(selectedDates.filter(date => date !== monthValue));
    }
  };

  const handleSortChange = (sortType: 'date' | 'location') => {
    const newSortByDate = sortType === 'date';
    if (sortByDate !== newSortByDate) {
      setSortByDate!(newSortByDate);
      
      // Save updated sort to userSelections
      const updatedUserSelections = {
        ...userSelections,
        filters: {
          cityState: cityStateInput,
          postalCode: postalCodeInput,
          rangeInput,
          selectedDates,
          sortByDate: newSortByDate
        }
      };
      dispatch({ type: 'updateUserSelections', data: updatedUserSelections });
      
      loadEvents(false, sortType);
    }
  };

  // Handle filter toggles with mutual exclusion
  const handleLocationFilterToggle = () => {
    if (!locationFilterOpen) {
      // If opening location filter, close month year filter
      setMonthYearFilterOpen(false)
    }
    setLocationFilterOpen(!locationFilterOpen)
  }

  const handleMonthYearFilterToggle = () => {
    if (!monthYearFilterOpen) {
      // If opening month year filter, close location filter
      setLocationFilterOpen(false)
    }
    setMonthYearFilterOpen(!monthYearFilterOpen)
  }

  // Determine which events to display based on initial load vs filtered
  const getEventsToDisplay = (): IDateTimeEvent[] => {
    // Check if we should show only shared selection using passed prop
    if (shouldShowOnlyShared && sharedDateTimeEvent) {
      // Always show the shared event from state, even if deselected
      return [sharedDateTimeEvent];
    }

    // If filters have been applied, show all filtered results
    if (hasAppliedFilters) {
      return dateTimeEventList;
    }

    // If initial load and user hasn't clicked "show all", limit to 10
    if (!hasAppliedFilters && !showAllInitialEvents && dateTimeEventList.length > 10) {
      return dateTimeEventList.slice(0, 10);
    }

    // Otherwise show all events
    return dateTimeEventList;
  };

  const shouldShowAllProgramsButton = (): boolean => {
    return !hasAppliedFilters && !showAllInitialEvents && initialEventList.length > 10;
  };

  // Hide filters and sorting when showing only shared selection.
  const hideFiltersAndSort = shouldShowOnlyShared;

  // Called if user clicks 'show all programs' CTA in the shared selection view (where only shared card is visible)
  const handleShowAllProgramsInternal = () => {
    // Call the dispatch logic from props
    handleShowAllPrograms();
    // Reset filters
    setCityStateInput?.('');
    setPostalCodeInput?.('');
    setRangeInput?.('');
    setSelectedDates?.([]);
    setHasAppliedFilters(false);
    setShowAllInitialEvents(true);
    // Reload events without filters
    loadEvents(true);
  };

  const handleResetFilters = async () => {
    // Clear all filter inputs
    setCityStateInput?.('');
    setPostalCodeInput?.('');
    setRangeInput?.('');
    setSelectedDates?.([]);
    setHasAppliedFilters(false);

    // Close any open filter dropdowns
    setLocationFilterOpen(false);
    setMonthYearFilterOpen(false);

    // Clear filters from userSelections context
    const updatedUserSelections = {
      ...userSelections,
      filters: {
        cityState: '',
        postalCode: '',
        rangeInput: '',
        selectedDates: [],
        sortByDate: true
      }
    };
    dispatch({ type: 'updateUserSelections', data: updatedUserSelections });

    // Reload events without filters by calling loadEvents directly with empty filters
    setIsLoading(true);
    try {
      const events = await fetchEventDetails({});
      setDateTimeEventList(events);
    } catch (err) {
      console.error('Error fetching event details:', err);
    } finally {
      setIsLoading(false);
    }
  };


  if (currentStep === targetStep) {
    return (
      <div>
        <section className="app-section-header">
          <div className="app-section-header-inner">
            <h1 className="app-section-title">{title}</h1>
            <p className="app-section-description">{description}</p>
            {/*Filters - Hide when showing only shared selection*/}
            <div>
              {!hideFiltersAndSort &&
                <div className="filters">
                  {/*Location Filter*/}
                  {currentStep === StepConstants.DateTimeSpecialtyProgramStep && eventRegistrationRefreshModel.distanceUnit &&
                    <LocationFilter
                      cityStateInput={cityStateInput}
                      postalCodeInput={postalCodeInput}
                      rangeInput={rangeInput}
                      onCityStateChange={setCityStateInput}
                      onPostalCodeChange={setPostalCodeInput}
                      onRangeChange={setRangeInput}
                      isOpen={locationFilterOpen}
                      onToggle={handleLocationFilterToggle}
                      onApply={(city, postal) => onApplyFiltersButtonClick(false, undefined, city, postal)}
                      isApplying={isLoadingResultsAfterApplyingFilters}
                    />
                  }
                  <MonthYearFilter
                    selectedDates={selectedDates}
                    onDateSelectionChange={handleDateSelection}
                    isOpen={monthYearFilterOpen}
                    onToggle={handleMonthYearFilterToggle}
                  />

                  <button onClick={() => onApplyFiltersButtonClick(false)} className="btn-primary filters-cta">
                    {epiProps.applyFiltersButtonText} {isLoadingResultsAfterApplyingFilters && <LoadingSpinner />}
                  </button>
                </div>
              }
              <div className="app-sort">
                {
                  !hideFiltersAndSort && currentStep === StepConstants.DateTimeSpecialtyProgramStep &&
                  <>
                    <p><strong>{epiProps.sortResultsByText}</strong></p>
                    <div className="app-sort-actions">
                      <button className={`app-sort-button ${sortByDate ? "active" : ""}`}
                        onClick={() => handleSortChange('date')}>
                        {epiProps.sortByDateText}
                      </button>
                    </div>
                    {
                      allEventsVirtual(dateTimeEventList) === false &&
                      <button className={`app-sort-button ${sortByDate == false ? "active" : ""}`}
                        onClick={() => handleSortChange('location')}>
                        {epiProps.sortByLocationText}
                      </button>
                    }
                  </>
                }
                <p><strong>{epiProps.showingNumberOfProgramsText
                  ? epiProps.showingNumberOfProgramsText.replace('{0}', getEventsToDisplay().length.toString()).replace('{1}', initialEventList.length.toString())
                  : `Showing ${getEventsToDisplay().length} of ${initialEventList.length} programs`}</strong></p>
              </div>
            </div>
          </div>
        </section>
        <ContentWrapper>
          <MainContent>

            {/* Show All button when displaying only shared selection */}
            {shouldShowOnlyShared && (
              <button
                onClick={handleShowAllProgramsInternal}
                className="btn-tertiary card-showall-btn"
              >
                {epiProps.showAllProgramsCtaTextDateTimeSteps}
              </button>
            )}
            {/*Show reset filters button when filters are applied and there are results.*/}
            {
              hasAppliedFilters && !isLoading && dateTimeEventList.length > 0 &&
              <button
                onClick={handleResetFilters}
                className="btn-tertiary card-showall-btn">
                {epiProps.resetFiltersCtaTextDateTimeStep}
              </button>
            }
            {
              programName && programName.length > 0 &&
              <div className="card card-grey">
                <h2 className="card-header-title">{programName}</h2>
                {dateTimeStepIntroMessage &&
                  <p className="card-intro-item">{dateTimeStepIntroMessage}</p>
                }
              </div>
            }

            {/* Loading indicator */}
            {isLoading && <p>Loading events...</p>}

            {/* Events list */}
            {!isLoading && dateTimeEventList.length > 0 ? (
              <React.Fragment>
                {getEventsToDisplay().map((event, index) => (
                  <React.Fragment key={event.eventNumber || index}>
                    <DateTimeCard
                      dateTimeEvent={event}
                      onSelect={handleEventSelection}
                      isSelected={isEventSelected(event.eventNumber)}
                    />
                  </React.Fragment>
                ))}

                {/* Show All Programs button - 
                  Displays if no filters are applied and results are not filtered to shared card,
                  and there are more than 10 cards. */}
                {(shouldShowAllProgramsButton() && shouldShowOnlyShared === false) && (
                  <button
                    onClick={() => setShowAllInitialEvents(true)}
                    className="btn-primary card-showall-btn"
                  >
                    {epiProps.showAllProgramsCtaTextDateTimeSteps}
                  </button>
                )}
              </React.Fragment>
            ) : !isLoading && (
              <div className="app-page-no-results">
                {initialEventList.length === 0
                  ? <p>{epiProps.noDateTimeEventsMessage}</p>
                  :
                  //No Filter Results Message + CTAs
                  <div className="stack-lg">
                    <div className="rich-text" dangerouslySetInnerHTML={{ __html: epiProps.noEventsMatchFiltersMessage }} />
                    <div className="button-list">
                      <button className="btn-primary button-list-item" onClick={handleResetFilters}>{epiProps.resetFiltersCtaTextDateTimeStep}</button>
                      {epiProps.noFilterResultsCtaButtonDateTimeStep && epiProps.noFilterResultsCtaButtonDateTimeStep.href && (
                        <a
                          href={resolveUrl(epiProps.noFilterResultsCtaButtonDateTimeStep.href)}
                          target={epiProps.noFilterResultsCtaButtonDateTimeStep.target}
                          className="btn-tertiary button-list-item"
                          title={epiProps.noFilterResultsCtaButtonDateTimeStep.title}
                        >
                          {epiProps.noFilterResultsCtaButtonDateTimeStep.text}
                        </a>
                      )}
                    </div>
                  </div>
                }
              </div>
            )}
          </MainContent>
          <SideBarContent>
            <ProgramSummary
              userSelections={userSelections}
              onContinue={handleContinueWithValidation}
              showValidationMessage={showValidationMessage}
              validationMessage={validationMessage || epiProps.dateTimeStepValidationMessage || "Please select a date and time to continue."}
              showShareSelectionsModalButton={true}
              onShowShareSelectionsModal={() => setShowShareSelectionsModal(true)}
            />
          </SideBarContent>
        </ContentWrapper>

        <Modal
          isOpen={showShareSelectionsModal}
          onClose={() => setShowShareSelectionsModal(false)}
          title={epiProps.shareSelectionsModalHeading}
          size="fullscreen"
          additionalClassName="share-modal"
        >
          <ShareSelections />
        </Modal>
      </div>
    )
  }

  return null
}
